import type { IconName, IconVariant, ReadMoreProps } from '@lemonade-hq/uniclient-theme';
import type { FormattedText, GridResponsiveColumns } from '../utils';
import type { InterpolableTextProps } from './interpolable_text';
import type { TagComposerProps } from './tag';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

export interface ContentListItemProps {
  readonly text: FormattedText | InterpolableTextProps;
  readonly bullet?:
    | 'default'
    | 'diamond'
    | 'indent'
    | 'none'
    | 'numeric'
    | { readonly name: IconName; readonly variant?: IconVariant };
  readonly sideTag?: TagComposerProps;
}

export enum ContentItemType {
  Item = 'item',
  Separator = 'separator',
  Grid = 'grid',
}

export interface ContentItem<TType extends ContentItemType> {
  readonly type?: TType;
}

export interface ContentItemProps extends ContentItem<ContentItemType.Item> {
  readonly title?: InterpolableTextProps;
  readonly subtitle?: string;
  readonly paragraph?: FormattedText | InterpolableTextProps;
  readonly sideTag?: TagComposerProps;
  readonly list?: ContentListItemProps[];
  readonly disclaimer?: InterpolableTextProps;
  readonly size?: 'md' | 'sm';
  readonly disabled?: boolean;
}
export interface ContentGridItemProps extends ContentItem<ContentItemType.Grid> {
  readonly items: ContentItemProps[];
}

export interface ContentProps extends UniclientBaseComponentProps {
  readonly columns?: GridResponsiveColumns;
  /** @optional Card styling: 'card' (regular background) or 'highlighted' (pending background) */
  readonly variant?: 'card' | 'highlighted';
  readonly collapseOptions?: ReadMoreProps;
  readonly contentSizeGap?: 'md' | 'sm';
  readonly items: (ContentGridItemProps | ContentItem<ContentItemType.Separator> | ContentItemProps)[];
}

export type WizardContentProps = ContentProps & UniclientWizardComponentBase<'content'>;

export function content(props: Omit<WizardContentProps, 'type'>): WizardContentProps {
  return { ...props, type: 'content' };
}

export const contentItem = (props: ContentItemProps): ContentItemProps => ({
  type: ContentItemType.Item,
  ...props,
});

export const contentSeparator = (): ContentItem<ContentItemType.Separator> => ({ type: ContentItemType.Separator });

export const contentGrid = (items: ContentItemProps[]): ContentGridItemProps => ({
  type: ContentItemType.Grid,
  items,
});

content.item = contentItem;
content.separator = contentSeparator;
content.grid = contentGrid;
